// MentorDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, Mail, Phone, Building, Briefcase, Users, 
  Award, Clock, User, ChevronRight, CheckCircle, Calendar,
  Star, FileText, BadgeCheck
} from 'lucide-react';
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import './MentorDetail.css';

// Function to convert slug back to full name
const slugToName = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Function to get image URL with fallback
const getImageUrl = (profilePicture) => {
  if (!profilePicture) return null;
  
  // If it's a full URL, use it as is
  if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
    return profilePicture;
  }
  
  // If it's a relative path, prepend the API base URL
  if (profilePicture.startsWith('/')) {
    return `http://145.79.0.94:8000${profilePicture}`;
  }
  
  // If it's a relative path without leading slash
  return `http://145.79.0.94:8000/${profilePicture}`;
};

export default function MentorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMentorDetails = async () => {
      try {
        const fullName = slugToName(slug);
        const encodedName = encodeURIComponent(fullName);
        const response = await fetch(`http://145.79.0.94:8000/api/mentor/mentor-by-name/${encodedName}/`);
        const data = await response.json();
        
        if (data.status && data.data) {
          setMentor(data.data);
        } else {
          setError('Mentor not found');
        }
      } catch (err) {
        setError('Error fetching mentor details');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchMentorDetails();
    }
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="mentor-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading mentor details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !mentor) {
    return (
      <>
        <Navbar />
        <div className="mentor-detail-error">
          <h2>Mentor Not Found</h2>
          <p>{error || 'The mentor you are looking for does not exist.'}</p>
          <Link to="/mentor" className="back-button">Back to Mentors</Link>
        </div>
        <Footer />
      </>
    );
  }

  const imageUrl = getImageUrl(mentor.profile_picture);

  return (
    <>
      <Helmet>
        <title>{mentor.full_name} — Mentor Profile</title>
        <meta name="description" content={`Learn more about ${mentor.full_name}, a mentor with ${mentor.years_of_experience} years of experience.`} />
      </Helmet>

      <Navbar />

      <main className="mentor-detail-page">
        {/* ── BACK BUTTON ── */}
        <div className="mentor-detail-back">
          <div className="container">
            <button onClick={() => navigate('/mentor')} className="back-btn">
              <ArrowLeft size={16} />
              Back to Mentors
            </button>
          </div>
        </div>

        {/* ── PROFILE HEADER ── */}
        <section className="mentor-detail-header">
          <div className="container">
            <div className="mentor-detail-profile">
              <div className="mentor-detail-avatar">
                {imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={mentor.full_name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="mentor-detail-avatar-placeholder">
                          ${mentor.full_name?.charAt(0) || 'M'}
                        </div>
                      `;
                    }}
                  />
                ) : (
                  <div className="mentor-detail-avatar-placeholder">
                    {mentor.full_name?.charAt(0) || 'M'}
                  </div>
                )}
              </div>
              <div className="mentor-detail-info">
                <div className="mentor-detail-badge">
                  <BadgeCheck size={18} />
                  <span>Verified Mentor</span>
                </div>
                <h1 className="mentor-detail-name">{mentor.full_name}</h1>
                <p className="mentor-detail-title">{mentor.current_company || 'Independent Mentor'}</p>
                <div className="mentor-detail-meta">
                  <span className="meta-item">
                    <Briefcase size={14} />
                    {mentor.years_of_experience || 0} years experience
                  </span>
                  <span className="meta-item">
                    <Users size={14} />
                    {mentor.current_trainees || 0} current trainees
                  </span>
                  <span className="meta-item">
                    <Users size={14} />
                    Max {mentor.max_trainees || 'Unlimited'} trainees
                  </span>
                  <span className={`meta-item status-${mentor.mentorship_status}`}>
                    <Clock size={14} />
                    {mentor.mentorship_status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CONTACT & DETAILS ── */}
        <section className="mentor-detail-content">
          <div className="container">
            <div className="mentor-detail-grid">
              {/* Left Column - Contact Info */}
              <div className="mentor-detail-contact">
                <h3>Contact Information</h3>
                <div className="contact-items">
                  <div className="contact-item">
                    <Mail size={16} />
                    <span>{mentor.email}</span>
                  </div>
                  {mentor.phone_number && (
                    <div className="contact-item">
                      <Phone size={16} />
                      <span>{mentor.phone_number}</span>
                    </div>
                  )}
                  {mentor.current_company && (
                    <div className="contact-item">
                      <Building size={16} />
                      <span>{mentor.current_company}</span>
                    </div>
                  )}
                </div>

                {/* Mentor Status */}
                <div className="mentor-status-section">
                  <h4>Mentorship Status</h4>
                  <div className="status-info">
                    <div className="status-item">
                      <span className="status-label">Status:</span>
                      <span className={`status-value ${mentor.mentorship_status}`}>
                        {mentor.mentorship_status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Admin Status:</span>
                      <span className="status-value">{mentor.admin_status || 'Pending'}</span>
                    </div>
                    {mentor.admin_comments && (
                      <div className="status-item">
                        <span className="status-label">Admin Comments:</span>
                        <span className="status-value">{mentor.admin_comments}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Trainee Stats */}
                <div className="mentor-stats-mini">
                  <div className="stat-mini">
                    <Users size={16} />
                    <div>
                      <span className="stat-mini-value">{mentor.current_trainees || 0}</span>
                      <span className="stat-mini-label">Current Trainees</span>
                    </div>
                  </div>
                  <div className="stat-mini">
                    <Users size={16} />
                    <div>
                      <span className="stat-mini-value">{mentor.max_trainees || '∞'}</span>
                      <span className="stat-mini-label">Max Capacity</span>
                    </div>
                  </div>
                  <div className="stat-mini">
                    <Star size={16} />
                    <div>
                      <span className="stat-mini-value">{mentor.years_of_experience || 0}+</span>
                      <span className="stat-mini-label">Years Experience</span>
                    </div>
                  </div>
                </div>

                {/* Member Since */}
                <div className="mentor-member-since">
                  <Calendar size={16} />
                  <span>Member since {formatDate(mentor.created_at)}</span>
                </div>

                <div className="mentor-apply-section">
                  <Link to="/join-ecosystem" className="apply-btn">
                    Request Mentorship <ArrowLeft size={16} />
                  </Link>
                </div>
              </div>

              {/* Right Column - Details */}
              <div className="mentor-detail-about">
                <h3>About {mentor.full_name.split(' ')[0]}</h3>
                <p>
                  {mentor.full_name} is a dedicated mentor with {mentor.years_of_experience} years of experience 
                  in {mentor.current_company || 'the industry'}. Passionate about guiding the next generation 
                  of professionals, {mentor.full_name.split(' ')[0]} brings practical insights and real-world 
                  expertise to every mentorship engagement.
                </p>

                {/* Specializations */}
                {mentor.specializations && mentor.specializations.length > 0 ? (
                  <div className="mentor-specializations-detail">
                    <h4>Specializations</h4>
                    <div className="specializations-list">
                      {mentor.specializations.map((spec, idx) => (
                        <div key={idx} className="specialization-item">
                          <div className="specialization-header">
                            <span className="specialization-name">{spec.department_name}</span>
                            <span className={`specialization-primary ${spec.is_primary_specialization ? 'primary' : ''}`}>
                              {spec.is_primary_specialization ? 'Primary' : 'Secondary'}
                            </span>
                          </div>
                          <div className="specialization-details">
                            <span className="specialization-level">Level: {spec.level_name}</span>
                            <span className="specialization-experience">
                              {spec.years_of_experience_in_specialization} years
                            </span>
                            <span className="specialization-capacity">
                              Max {spec.max_trainees_for_specialization} trainees
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mentor-specializations-detail">
                    <h4>Specializations</h4>
                    <p className="no-data">No specializations listed yet.</p>
                  </div>
                )}

                {/* Certifications */}
                {mentor.certifications && mentor.certifications.length > 0 ? (
                  <div className="mentor-certifications-detail">
                    <h4>Certifications</h4>
                    <div className="certifications-list">
                      {mentor.certifications.map((cert, idx) => (
                        <div key={idx} className="certification-item">
                          <Award size={16} />
                          <div className="certification-content">
                            <span className="cert-name">{cert.certification_name || cert.certification_type_display}</span>
                            <span className="cert-issuer">{cert.issuing_organization || 'Industry Certified'}</span>
                            <div className="cert-details">
                              <span className="cert-type">{cert.certification_type_display}</span>
                              {cert.issued_date && (
                                <span className="cert-date">Issued: {formatDate(cert.issued_date)}</span>
                              )}
                              {cert.expiry_date && (
                                <span className={`cert-expiry ${cert.is_expired ? 'expired' : 'valid'}`}>
                                  {cert.is_expired ? 'Expired' : 'Valid until: ' + formatDate(cert.expiry_date)}
                                </span>
                              )}
                              {cert.document && (
                                <a href={cert.document} target="_blank" rel="noopener noreferrer" className="cert-document-link">
                                  <FileText size={12} /> View Document
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mentor-certifications-detail">
                    <h4>Certifications</h4>
                    <p className="no-data">No certifications listed yet.</p>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="mentor-stats-detail">
                  <div className="stat-box">
                    <span className="stat-number">{mentor.current_trainees || 0}</span>
                    <span className="stat-label">Current Trainees</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{mentor.years_of_experience || 0}+</span>
                    <span className="stat-label">Years Experience</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{mentor.max_trainees || '∞'}</span>
                    <span className="stat-label">Max Trainees</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{mentor.specializations?.length || 0}</span>
                    <span className="stat-label">Specializations</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number">{mentor.certifications?.length || 0}</span>
                    <span className="stat-label">Certifications</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-number" style={{ color: mentor.mentorship_status === 'active' ? '#4ade80' : '#fbbf24' }}>
                      {mentor.mentorship_status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                    <span className="stat-label">Status</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="mentor-detail-cta">
          <div className="container">
            <div className="cta-box">
              <h2>Ready to Start Your Mentorship Journey?</h2>
              <p>Connect with {mentor.full_name} and take the next step in your career development.</p>
              <Link to="/join-ecosystem" className="cta-button">
                Request Mentorship <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}